/**
 * TAX_DEEP_DIVE_LandingPage_v02O (React + TypeScript + React-Bootstrap)
 * Version: v02O
 * Date: 2025-09-01 (America/Denver)
 *
 * Changes vs renters v02O:
 * - Rethemed for "Tax Deep Dive" (slides, copy, badges).
 * - Lead fields tailored for tax review/optimization intake.
 * - Inline FAQ + simple component; no external imports.
 * - Same wizard/vertical UX, blur validation, and modular payload pipeline.
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v02O";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const ADVISOR_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/TAX-DeepDive/";

/* ============================================================
   TYPES
============================================================ */
type FilingStatus = "" | "single" | "marriedJoint" | "marriedSeparate" | "headOfHousehold" | "qualifyingWidow";
type BusinessType = "" | "none" | "soleProp" | "llc" | "sCorp" | "cCorp" | "partnership";
type IncomeBracket = "" | "<50k" | "50-100k" | "100-250k" | "250-500k" | "500k+";
type InterestArea = "" | "deductions" | "credits" | "entity" | "retirement" | "realEstate" | "stockOptions" | "rAndD" | "crypto";

/** Raw lead payload (typed for app state) */
interface ILeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string;

  // Basics
  state: string;
  taxYear: string;            // e.g., "2024"
  filingStatus: FilingStatus;
  dependentsCount: number;

  // Income & Business
  incomeBracket: IncomeBracket;
  businessType: BusinessType;
  has1099Income: boolean;
  hasW2Income: boolean;
  hasK1Income: boolean;

  // Interests / Focus
  primaryInterest: InterestArea;
  secondaryInterest?: InterestArea;
  itemizeDeductions: "" | "yes" | "no" | "unsure";

  // Issues / Goals
  receivedIRSNotice: "" | "yes" | "no";
  painPoints?: string;        // open text
  goals?: string;             // open text

  // Scheduling
  preferredContactTime?: string; // open text (e.g., "Weekdays after 3pm MT")

  // UTM/meta
  utm?: { source?: string; medium?: string; campaign?: string };
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s ?? "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
const dash = (v?: string) => (v && v.trim().length ? v : "—");

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_192878728_Preview.opt.jpg",
    alt: "Advisor reviewing a tax plan with charts and notes",
    title: "Cut Through Tax Complexity",
    subline: "Expert review to uncover missed deductions, credits, and entity savings.",
    smallCaption: "Optimization starts here.",
  },
  {
    src: imagePath + "AdobeStock_601618520_Preview.opt.jpg",
    alt: "Calculator and documents representing tax planning",
    title: "Keep More Of What You Earn",
    subline: "From W-2 to K-1, we align strategy with your real-world income.",
    smallCaption: "Personalized strategies.",
  },
  {
    src: imagePath + "AdobeStock_185369119_Preview.opt.jpg",
    alt: "Laptop with spreadsheet and coffee on a desk",
    title: "Confident, Compliant, Proactive",
    subline: "We map next steps you can actually use this year.",
    smallCaption: "Clear actions, zero guesswork.",
  },
];

/* ============================================================
   INLINE FAQ (simple component)
============================================================ */
type FAQ = { id: string; question: string; answer: string; category?: string };
const taxDeepDiveFAQs: FAQ[] = [
  {
    id: "q1",
    question: "What is a Tax Deep Dive?",
    answer: "A structured review of your most recent return, income sources, and entity to identify missed deductions, credits, and planning opportunities for this year.",
    category: "General",
  },
  {
    id: "q2",
    question: "Do I need last year’s return?",
    answer: "It helps. We can start with your answers and then refine after we review your prior return and any IRS/state notices.",
    category: "Getting Started",
  },
  {
    id: "q3",
    question: "Is this the same as filing my taxes?",
    answer: "No—this is a planning and optimization session. If you need filing services, we can connect you with a licensed preparer.",
    category: "Scope",
  },
  {
    id: "q4",
    question: "Who benefits most?",
    answer: "W-2 + 1099 earners, small business owners (LLC/S-Corp), real-estate investors, equity-comp recipients, and anyone with big life changes.",
    category: "Fit",
  },
  {
    id: "q5",
    question: "How soon do I get findings?",
    answer: "We provide an initial summary after intake, then a deeper recommendations sheet after document review.",
    category: "Timeline",
  },
];

const SimpleFAQ: React.FC<{ faqs: FAQ[]; openFirst?: boolean }> = ({ faqs, openFirst }) => {
  const [openId, setOpenId] = useState<string | null>(openFirst && faqs[0] ? faqs[0].id : null);
  return (
    <div className="d-grid gap-2">
      {faqs.map((f) => {
        const isOpen = openId === f.id;
        return (
          <Card key={f.id} className="border-0 shadow-sm">
            <Card.Header
              role="button"
              className="bg-white fw-semibold d-flex justify-content-between align-items-center"
              onClick={() => setOpenId(isOpen ? null : f.id)}
            >
              <span>{f.question}</span>
              <span className="small text-muted">{isOpen ? "Hide" : "Show"}</span>
            </Card.Header>
            {isOpen && (
              <Card.Body className="pt-2">
                <div className="text-body">{f.answer}</div>
                {f.category && <div className="mt-2 small text-muted">Category: {f.category}</div>}
              </Card.Body>
            )}
          </Card>
        );
      })}
    </div>
  );
};

/* ============================================================
   MAIN
============================================================ */
export const TAX_DEEP_DIVE_LandingPage_v02O: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Basics
  const [state, setState] = useState("");
  const [taxYear, setTaxYear] = useState("2024"); // default to last year
  const [filingStatus, setFilingStatus] = useState<FilingStatus>("");
  const [dependentsCount, setDependentsCount] = useState<number>(0);

  // Income & Business
  const [incomeBracket, setIncomeBracket] = useState<IncomeBracket>("");
  const [businessType, setBusinessType] = useState<BusinessType>("");
  const [has1099Income, setHas1099Income] = useState<boolean>(false);
  const [hasW2Income, setHasW2Income] = useState<boolean>(true);
  const [hasK1Income, setHasK1Income] = useState<boolean>(false);

  // Interests
  const [primaryInterest, setPrimaryInterest] = useState<InterestArea>("");
  const [secondaryInterest, setSecondaryInterest] = useState<InterestArea>("");
  const [itemizeDeductions, setItemizeDeductions] = useState<"" | "yes" | "no" | "unsure">("");

  // Issues / Goals
  const [receivedIRSNotice, setReceivedIRSNotice] = useState<"" | "yes" | "no">("");
  const [painPoints, setPainPoints] = useState("");
  const [goals, setGoals] = useState("");

  // Scheduling
  const [preferredContactTime, setPreferredContactTime] = useState("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // Keep the exact data sent (for confirmation display)
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Basics", "Income & Interests", "Issues & Goals", "Confirmation"] as const;

  /* ---------- Touched + validation helpers (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  // Optional-field validation props: green when filled & valid; neutral when blank; never red on blank.
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators (blank optional fields pass)
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    state: state !== "",
    taxYear: /^\d{4}$/.test(taxYear),
    filingStatus: filingStatus !== "",
    dependentsCount: Number.isFinite(dependentsCount) && dependentsCount >= 0 && dependentsCount <= 20,

    incomeBracket: incomeBracket !== "",
    businessType: businessType !== "",
    primaryInterest: primaryInterest !== "",
    itemizeDeductions: itemizeDeductions !== "",
    receivedIRSNotice: receivedIRSNotice !== "",

    // Optionals must pass format when present
    secondaryInterest: secondaryInterest === "" || (["deductions","credits","entity","retirement","realEstate","stockOptions","rAndD","crypto"] as InterestArea[]).includes(secondaryInterest),
    painPoints: painPoints.trim().length === 0 || painPoints.trim().length >= 2,
    goals: goals.trim().length === 0 || goals.trim().length >= 2,
    preferredContactTime: preferredContactTime.trim().length === 0 || preferredContactTime.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.fullName && V.email && V.phone &&
      V.state && V.taxYear && V.filingStatus && V.dependentsCount &&
      V.incomeBracket && V.businessType &&
      V.primaryInterest && V.itemizeDeductions &&
      V.receivedIRSNotice &&
      V.secondaryInterest && V.painPoints && V.goals && V.preferredContactTime
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.state && V.taxYear && V.filingStatus && V.dependentsCount;
      case 1:
        return V.incomeBracket && V.businessType && V.primaryInterest && V.itemizeDeductions;
      case 2:
        return V.receivedIRSNotice && V.secondaryInterest && V.painPoints && V.goals && V.preferredContactTime;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","state","taxYear","filingStatus","dependentsCount"]);
    else if (currentStep === 1) touchMany(["incomeBracket","businessType","primaryInterest","itemizeDeductions"]);
    else if (currentStep === 2) touchMany(["receivedIRSNotice","secondaryInterest","painPoints","goals","preferredContactTime"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT (MODULARIZED)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    return {
      // Contact
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      // Basics
      state: String(state),
      taxYear: String(taxYear),
      filingStatus,
      dependentsCount: Number(dependentsCount),

      // Income/Business
      incomeBracket,
      businessType,
      has1099Income,
      hasW2Income,
      hasK1Income,

      // Interests
      primaryInterest,
      secondaryInterest: secondaryInterest || "",
      itemizeDeductions,

      // Issues/Goals
      receivedIRSNotice,
      painPoints: painPoints.trim(),
      goals: goals.trim(),

      // Scheduling
      preferredContactTime: preferredContactTime.trim(),

      // UTM/meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.tax.deepdive",
      itemClass: "consult.request",
      itemType: "landing.taxdeepdive",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch { resJson = null; }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all prior fields so feedback appears if invalid
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      state: true, taxYear: true, filingStatus: true, dependentsCount: true,
      incomeBracket: true, businessType: true, primaryInterest: true, itemizeDeductions: true,
      receivedIRSNotice: true, secondaryInterest: true, painPoints: true, goals: true, preferredContactTime: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    // 1) Build typed app payload
    const appPayload = buildLeadPayload();

    // 2) Convert to string-only wire payload
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      // 3) Post
      const status = await postLead(wirePayload);

      // 4) Update UI
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      // Persist what was sent so the confirmation reflects the exact data
      setLastSubmittedApp(appPayload);

      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp the active slide index to avoid accidental out-of-range access
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Tax <span className="text-primary">Deep Dive</span>
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Talk to an advisor:</div>
              <Button variant="outline-primary" href="tel:3033374433">{ADVISOR_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Strategy First</Badge>
                  <Badge bg="light" text="dark">Actionable Steps</Badge>
                  <Badge bg="light" text="dark">Licensed Pros</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Start My Deep Dive</Button>
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Get Your Tax Deep Dive</div>
                      <small className="text-body-secondary">Tell us about your situation—no obligation.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM START */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & BASICS */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Basics</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={12}>
                                    <Form.Label htmlFor="fullName">Full Name</Form.Label>
                                    <Form.Control
                                      id="fullName"
                                      value={fullName}
                                      onChange={(e) => setFullName(e.target.value)}
                                      placeholder="Jane Doe"
                                      required
                                      {...vp("fullName", V.fullName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(555) 123-4567"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="taxYear">Tax Year</Form.Label>
                                    <Form.Control
                                      id="taxYear"
                                      inputMode="numeric"
                                      value={taxYear}
                                      onChange={(e) => setTaxYear(e.target.value)}
                                      placeholder="YYYY"
                                      required
                                      {...vp("taxYear", V.taxYear)}
                                    />
                                    <div className="form-text">Usually last filed year.</div>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="filingStatus">Filing Status</Form.Label>
                                    <Form.Select
                                      id="filingStatus"
                                      value={filingStatus}
                                      onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
                                      required
                                      {...vp("filingStatus", V.filingStatus)}
                                    >
                                      <option value="">Select</option>
                                      <option value="single">Single</option>
                                      <option value="marriedJoint">Married Filing Jointly</option>
                                      <option value="marriedSeparate">Married Filing Separately</option>
                                      <option value="headOfHousehold">Head of Household</option>
                                      <option value="qualifyingWidow">Qualifying Widow(er)</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="dependents">Dependents</Form.Label>
                                    <Form.Control
                                      id="dependents"
                                      inputMode="numeric"
                                      value={dependentsCount}
                                      onChange={(e) => setDependentsCount(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("dependentsCount", V.dependentsCount)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–20 allowed.</Form.Control.Feedback>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: INCOME & INTERESTS */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Income &amp; Focus Areas</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="incomeBracket">Household Income</Form.Label>
                                    <Form.Select
                                      id="incomeBracket"
                                      value={incomeBracket}
                                      onChange={(e) => setIncomeBracket(e.target.value as IncomeBracket)}
                                      required
                                      {...vp("incomeBracket", V.incomeBracket)}
                                    >
                                      <option value="">Select</option>
                                      <option value="<50k">Under $50k</option>
                                      <option value="50-100k">$50k–$100k</option>
                                      <option value="100-250k">$100k–$250k</option>
                                      <option value="250-500k">$250k–$500k</option>
                                      <option value="500k+">$500k+</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="businessType">Business / Entity</Form.Label>
                                    <Form.Select
                                      id="businessType"
                                      value={businessType}
                                      onChange={(e) => setBusinessType(e.target.value as BusinessType)}
                                      required
                                      {...vp("businessType", V.businessType)}
                                    >
                                      <option value="">Select</option>
                                      <option value="none">No business</option>
                                      <option value="soleProp">Sole Proprietor</option>
                                      <option value="llc">LLC</option>
                                      <option value="sCorp">S-Corp</option>
                                      <option value="cCorp">C-Corp</option>
                                      <option value="partnership">Partnership</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={12}>
                                    <div className="d-flex flex-wrap gap-4 pt-1">
                                      <Form.Check
                                        id="hasW2"
                                        type="checkbox"
                                        label="W-2 Income"
                                        checked={hasW2Income}
                                        onChange={(e) => setHasW2Income(e.currentTarget.checked)}
                                      />
                                      <Form.Check
                                        id="has1099"
                                        type="checkbox"
                                        label="1099 Income"
                                        checked={has1099Income}
                                        onChange={(e) => setHas1099Income(e.currentTarget.checked)}
                                      />
                                      <Form.Check
                                        id="hasK1"
                                        type="checkbox"
                                        label="K-1 Income"
                                        checked={hasK1Income}
                                        onChange={(e) => setHasK1Income(e.currentTarget.checked)}
                                      />
                                    </div>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="primaryInterest">Primary Interest</Form.Label>
                                    <Form.Select
                                      id="primaryInterest"
                                      value={primaryInterest}
                                      onChange={(e) => setPrimaryInterest(e.target.value as InterestArea)}
                                      required
                                      {...vp("primaryInterest", V.primaryInterest)}
                                    >
                                      <option value="">Select</option>
                                      <option value="deductions">Deductions</option>
                                      <option value="credits">Credits</option>
                                      <option value="entity">Entity Optimization</option>
                                      <option value="retirement">Retirement/Deferral</option>
                                      <option value="realEstate">Real Estate</option>
                                      <option value="stockOptions">Stock Options/RSUs</option>
                                      <option value="rAndD">R&D / Cost Seg</option>
                                      <option value="crypto">Crypto</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="secondaryInterest">Secondary Interest (optional)</Form.Label>
                                    <Form.Select
                                      id="secondaryInterest"
                                      value={secondaryInterest}
                                      onChange={(e) => setSecondaryInterest(e.target.value as InterestArea)}
                                      {...vp("secondaryInterest", V.secondaryInterest)}
                                    >
                                      <option value="">None</option>
                                      <option value="deductions">Deductions</option>
                                      <option value="credits">Credits</option>
                                      <option value="entity">Entity Optimization</option>
                                      <option value="retirement">Retirement/Deferral</option>
                                      <option value="realEstate">Real Estate</option>
                                      <option value="stockOptions">Stock Options/RSUs</option>
                                      <option value="rAndD">R&D / Cost Seg</option>
                                      <option value="crypto">Crypto</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="itemize">Do you itemize deductions?</Form.Label>
                                    <Form.Select
                                      id="itemize"
                                      value={itemizeDeductions}
                                      onChange={(e) => setItemizeDeductions(e.target.value as "yes" | "no" | "unsure" | "")}
                                      required
                                      {...vp("itemizeDeductions", V.itemizeDeductions)}
                                    >
                                      <option value="">Select</option>
                                      <option value="yes">Yes</option>
                                      <option value="no">No (Standard Deduction)</option>
                                      <option value="unsure">Unsure</option>
                                    </Form.Select>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: ISSUES & GOALS */}
                          {currentStep === 2 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Issues &amp; Goals</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={12}>
                                    <Form.Label htmlFor="irsNotice">Have you received any IRS/state notices?</Form.Label>
                                    <Form.Select
                                      id="irsNotice"
                                      value={receivedIRSNotice}
                                      onChange={(e) => setReceivedIRSNotice(e.target.value as "" | "yes" | "no")}
                                      required
                                      {...vp("receivedIRSNotice", V.receivedIRSNotice)}
                                    >
                                      <option value="">Select</option>
                                      <option value="no">No</option>
                                      <option value="yes">Yes</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="prefTime">Preferred contact time (optional)</Form.Label>
                                    <Form.Control
                                      id="prefTime"
                                      value={preferredContactTime}
                                      onChange={(e) => setPreferredContactTime(e.target.value)}
                                      placeholder="e.g., Weekdays after 3pm MT"
                                      {...vp("preferredContactTime", V.preferredContactTime)}
                                    />
                                    
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="painPoints">What’s not working? (optional)</Form.Label>
                                    <Form.Control
                                      id="painPoints"
                                      as="textarea"
                                      rows={3}
                                      value={painPoints}
                                      onChange={(e) => setPainPoints(e.target.value)}
                                      placeholder="Surprises at tax time, entity confusion, messy bookkeeping, etc."
                                      {...vp("painPoints", V.painPoints)}
                                    />
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="goals">Goals for this year? (optional)</Form.Label>
                                    <Form.Control
                                      id="goals"
                                      as="textarea"
                                      rows={3}
                                      value={goals}
                                      onChange={(e) => setGoals(e.target.value)}
                                      placeholder="Lower effective tax rate, restructure entity, plan RSU sales, etc."
                                      {...vp("goals", V.goals)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls (no buttons on final step) */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Start My Deep Dive</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      /* ---------- VERTICAL (all-in-one) ---------- */
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & Basics */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Basics</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="fullNameV">Full Name</Form.Label>
                                      <Form.Control
                                        id="fullNameV"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Jane Doe"
                                        required
                                        {...vp("fullName", V.fullName)}
                                      />
                                      <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control
                                        id="emailV"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control
                                        id="phoneV"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(555) 123-4567"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">State</Form.Label>
                                      <Form.Select
                                        id="stateV"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="taxYearV">Tax Year</Form.Label>
                                      <Form.Control
                                        id="taxYearV"
                                        inputMode="numeric"
                                        value={taxYear}
                                        onChange={(e) => setTaxYear(e.target.value)}
                                        placeholder="YYYY"
                                        required
                                        {...vp("taxYear", V.taxYear)}
                                      />
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="filingStatusV">Filing Status</Form.Label>
                                      <Form.Select
                                        id="filingStatusV"
                                        value={filingStatus}
                                        onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
                                        required
                                        {...vp("filingStatus", V.filingStatus)}
                                      >
                                        <option value="">Select</option>
                                        <option value="single">Single</option>
                                        <option value="marriedJoint">Married Filing Jointly</option>
                                        <option value="marriedSeparate">Married Filing Separately</option>
                                        <option value="headOfHousehold">Head of Household</option>
                                        <option value="qualifyingWidow">Qualifying Widow(er)</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="dependentsV">Dependents</Form.Label>
                                      <Form.Control
                                        id="dependentsV"
                                        inputMode="numeric"
                                        value={dependentsCount}
                                        onChange={(e) => setDependentsCount(Number(e.target.value))}
                                        placeholder="0"
                                        required
                                        {...vp("dependentsCount", V.dependentsCount)}
                                      />
                                      <Form.Control.Feedback type="invalid">0–20 allowed.</Form.Control.Feedback>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Income & Interests */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Income &amp; Focus Areas</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="incomeBracketV">Household Income</Form.Label>
                                      <Form.Select
                                        id="incomeBracketV"
                                        value={incomeBracket}
                                        onChange={(e) => setIncomeBracket(e.target.value as IncomeBracket)}
                                        required
                                        {...vp("incomeBracket", V.incomeBracket)}
                                      >
                                        <option value="">Select</option>
                                        <option value="<50k">Under $50k</option>
                                        <option value="50-100k">$50k–$100k</option>
                                        <option value="100-250k">$100k–$250k</option>
                                        <option value="250-500k">$250k–$500k</option>
                                        <option value="500k+">$500k+</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="businessTypeV">Business / Entity</Form.Label>
                                      <Form.Select
                                        id="businessTypeV"
                                        value={businessType}
                                        onChange={(e) => setBusinessType(e.target.value as BusinessType)}
                                        required
                                        {...vp("businessType", V.businessType)}
                                      >
                                        <option value="">Select</option>
                                        <option value="none">No business</option>
                                        <option value="soleProp">Sole Proprietor</option>
                                        <option value="llc">LLC</option>
                                        <option value="sCorp">S-Corp</option>
                                        <option value="cCorp">C-Corp</option>
                                        <option value="partnership">Partnership</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={12}>
                                      <div className="d-flex flex-wrap gap-4 pt-1">
                                        <Form.Check
                                          id="hasW2V"
                                          type="checkbox"
                                          label="W-2 Income"
                                          checked={hasW2Income}
                                          onChange={(e) => setHasW2Income(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="has1099V"
                                          type="checkbox"
                                          label="1099 Income"
                                          checked={has1099Income}
                                          onChange={(e) => setHas1099Income(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="hasK1V"
                                          type="checkbox"
                                          label="K-1 Income"
                                          checked={hasK1Income}
                                          onChange={(e) => setHasK1Income(e.currentTarget.checked)}
                                        />
                                      </div>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="primaryInterestV">Primary Interest</Form.Label>
                                      <Form.Select
                                        id="primaryInterestV"
                                        value={primaryInterest}
                                        onChange={(e) => setPrimaryInterest(e.target.value as InterestArea)}
                                        required
                                        {...vp("primaryInterest", V.primaryInterest)}
                                      >
                                        <option value="">Select</option>
                                        <option value="deductions">Deductions</option>
                                        <option value="credits">Credits</option>
                                        <option value="entity">Entity Optimization</option>
                                        <option value="retirement">Retirement/Deferral</option>
                                        <option value="realEstate">Real Estate</option>
                                        <option value="stockOptions">Stock Options/RSUs</option>
                                        <option value="rAndD">R&D / Cost Seg</option>
                                        <option value="crypto">Crypto</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="secondaryInterestV">Secondary Interest (optional)</Form.Label>
                                      <Form.Select
                                        id="secondaryInterestV"
                                        value={secondaryInterest}
                                        onChange={(e) => setSecondaryInterest(e.target.value as InterestArea)}
                                        {...vp("secondaryInterest", V.secondaryInterest)}
                                      >
                                        <option value="">None</option>
                                        <option value="deductions">Deductions</option>
                                        <option value="credits">Credits</option>
                                        <option value="entity">Entity Optimization</option>
                                        <option value="retirement">Retirement/Deferral</option>
                                        <option value="realEstate">Real Estate</option>
                                        <option value="stockOptions">Stock Options/RSUs</option>
                                        <option value="rAndD">R&D / Cost Seg</option>
                                        <option value="crypto">Crypto</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="itemizeV">Do you itemize deductions?</Form.Label>
                                      <Form.Select
                                        id="itemizeV"
                                        value={itemizeDeductions}
                                        onChange={(e) => setItemizeDeductions(e.target.value as "yes" | "no" | "unsure" | "")}
                                        required
                                        {...vp("itemizeDeductions", V.itemizeDeductions)}
                                      >
                                        <option value="">Select</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No (Standard Deduction)</option>
                                        <option value="unsure">Unsure</option>
                                      </Form.Select>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Issues & Goals */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Issues &amp; Goals</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="irsNoticeV">Have you received any IRS/state notices?</Form.Label>
                                      <Form.Select
                                        id="irsNoticeV"
                                        value={receivedIRSNotice}
                                        onChange={(e) => setReceivedIRSNotice(e.target.value as "" | "yes" | "no")}
                                        required
                                        {...vp("receivedIRSNotice", V.receivedIRSNotice)}
                                      >
                                        <option value="">Select</option>
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="prefTimeV">Preferred contact time (optional)</Form.Label>
                                      <Form.Control
                                        id="prefTimeV"
                                        value={preferredContactTime}
                                        onChange={(e) => setPreferredContactTime(e.target.value)}
                                        placeholder="e.g., Weekdays after 3pm MT"
                                        {...vp("preferredContactTime", V.preferredContactTime)}
                                      />
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="painPointsV">What’s not working? (optional)</Form.Label>
                                      <Form.Control
                                        id="painPointsV"
                                        as="textarea"
                                        rows={3}
                                        value={painPoints}
                                        onChange={(e) => setPainPoints(e.target.value)}
                                        placeholder="Surprises at tax time, entity confusion, messy bookkeeping, etc."
                                        {...vp("painPoints", V.painPoints)}
                                      />
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="goalsV">Goals for this year? (optional)</Form.Label>
                                      <Form.Control
                                        id="goalsV"
                                        as="textarea"
                                        rows={3}
                                        value={goals}
                                        onChange={(e) => setGoals(e.target.value)}
                                        placeholder="Lower effective tax rate, restructure entity, plan RSU sales, etc."
                                        {...vp("goals", V.goals)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Start My Deep Dive</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about tax planning options. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                  {/* FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to optimize your taxes.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Share your situation</h6>
                  <p className="mb-0 text-body-secondary">Income, filing status, business/entity, and goals.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">We analyze & model</h6>
                  <p className="mb-0 text-body-secondary">Identify deductions, credits, and entity strategies.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">You get a plan</h6>
                  <p className="mb-0 text-body-secondary">Clear action items for this year—no guesswork.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Quick answers about Tax Deep Dive.</p>
            </Col>
          </Row>
          <SimpleFAQ faqs={taxDeepDiveFAQs} openFirst />
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Tax Deep Dive. All rights reserved.
              <div className="mt-2">
                Planning guidance only; not legal, tax, or investment advice. Services subject to licensing and availability.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {ADVISOR_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const incomeSources = [
    app.hasW2Income ? "W-2" : null,
    app.has1099Income ? "1099" : null,
    app.hasK1Income ? "K-1" : null,
  ].filter(Boolean).join(", ") || "—";

// QUICK FIX.............
// Before:
// const pretty = (s: string) => (s || "—");

// After:
const pretty = (s?: string | null) => (s && s.trim().length ? s : "—");
  const yesNoUnsure = (s: string) => (s === "yes" ? "Yes" : s === "no" ? "No" : s === "unsure" ? "Unsure" : "—");
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! An advisor will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          {/* Contact */}
          <Section title="Contact">
            <KV label="Name"  value={pretty(app.fullName)} />
            <KV label="Email" value={pretty(app.email)} />
            <KV label="Phone" value={pretty(app.phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3"))} />
          </Section>

          {/* Basics */}
          <Section title="Basics">
            <KV label="State"         value={pretty(app.state)} />
            <KV label="Tax Year"      value={pretty(app.taxYear)} />
            <KV label="Filing Status" value={cap(pretty(app.filingStatus))} />
            <KV label="Dependents"    value={String(app.dependentsCount)} />
          </Section>

          {/* Income & Business */}
          <Section title="Income & Business">
            <KV label="Income Bracket" value={pretty(app.incomeBracket)} />
            <KV label="Business Type"  value={pretty(app.businessType)} />
            <KV label="Income Sources" value={incomeSources} />
          </Section>

          {/* Interests */}
          <Section title="Focus Areas">
            <KV label="Primary Interest"   value={cap(pretty(app.primaryInterest))} />
            <KV label="Secondary Interest" value={cap(pretty(app.secondaryInterest || "")) || "—"} />
            <KV label="Itemize?"           value={yesNoUnsure(app.itemizeDeductions)} />
          </Section>

          {/* Issues & Goals */}
          <Section title="Issues & Goals">
            <KV label="IRS/State Notice" value={app.receivedIRSNotice === "yes" ? "Yes" : app.receivedIRSNotice === "no" ? "No" : "—"} />
            <KV label="Pain Points"      value={pretty(app.painPoints)} />
            <KV label="Goals"            value={pretty(app.goals)} />
            <KV label="Preferred Contact" value={pretty(app.preferredContactTime)} />
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {ADVISOR_PHONE_NUMBER}</Button>
          <Button
            variant="outline-secondary"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Back to top
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default TAX_DEEP_DIVE_LandingPage_v02O;
